<template>
	<main class="text-page">
		<div class="text-page__bg _ibg"></div>
		<div class="text-page__inner _cnt">
			<div class="section text-section">
				<div class="page-inside-top">
					<Transition>
						<div v-if="pageContent" class="wrapper">
							<div class="text-page__title _h1">
								{{ pageContent.title }}
							</div>
							<div class="text-page__line _line"><span></span></div>
							<div
								class="text-page__header"
								v-html="pageContent.header"
							/>
							<div
								class="text-page__content"
								v-html="pageContent.content"
							/>
						</div>
					</Transition>
				</div>
			</div>
		</div>
	</main>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
	name: 'TextPage',
	data() {
		return {
			pageContent: null,
			loading: false,
		};
	},
	computed: {
		...mapState('app', ['currentLanguage']),
	},
	watch: {
		'$route.fullPath': {
			immediate: true,
			handler() {
				this.loadPageContent();
			},
		},
		currentLanguage: {
			handler() {
				this.loadPageContent();
			},
		},
	},
	methods: {
		...mapActions('staticPages', ['fetchPageContent', 'fetchPages']),
		async loadPageContent() {
			try {
				this.loading = true;
				this.pageContent = null; // Clear old content while loading

				const langId = this.currentLanguage;
				const routeId = this.$route.params.id;
				const slug = this.$route.params.slug;
				if (routeId) {
					const content = await this.fetchPageContent({
						id: routeId,
						langId,
					});
					this.pageContent = content;
					return;
				}

				if (slug) {
					if (!this.$store.state.staticPages?.pages?.length) {
						await this.fetchPages(langId);
					}

					const page = this.$store.state.staticPages.pages.find(
							(page) => page.slug === slug
					);

					if (page?.id) {
						const content = await this.fetchPageContent({
							id: page.id,
							langId,
						});
						this.pageContent = content;
						return;
					}
				}

				this.$router.replace({ name: 'PageNotFound' });
			} catch (error) {
				console.error('Failed to load page content:', error);
			} finally {
				this.loading = false;
			}
		},
		back() {
			this.$router.go(-1);
		},
	},
};
</script>

<style lang="scss" scoped>
@use '@/assets/styles/mixins' as *;
@use '@/assets/styles/media' as *;
@use '@/assets/styles/components/classes' as *;

.text-page {
	@include header-indent;
	@include adaptiveValue('padding-top', 50, 25);
	@include adaptiveValue('padding-bottom', 50, 20);
	position: relative;
	position: relative;
	&__bg {
		pointer-events: none;
		z-index: -1;
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
	}
	&__title {
		text-align: center;
		&:not(:last-child) {
			margin-bottom: 10px;
		}
	}
	&__line {
		max-width: 710px;
		margin-left: auto;
		margin-right: auto;
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 30, 20);
		}
	}
	&__header {
		color: var(--primary-color);
	}
	&__content {
		color: var(--secondary-color);
		font-weight: 400;
		:deep(a) {
			color: var(--hint-color);
			transition: color 0.3s ease 0s;
			@media (any-hover: hover) {
				&:hover {
					color: var(--hint-hover-color);
				}
			}
		}
	}
}

.text-page strong {
	font-weight: bold;
}

.text-page em {
	font-style: italic;
}

.text-page ul {
	list-style: disc;
	padding-left: 30px;
	margin-bottom: 15px;
}

.text-page ol {
	list-style: decimal;
	padding-left: 30px;
	margin-bottom: 15px;
}

.text-page li {
	margin-bottom: 8px;
}

.text-page hr {
	border: 0;
	border-top: 1px solid var(--bg-third-color);
	margin: 20px 0;
}

.text-page th,
.text-page td {
	border-top: 1px solid var(--bg-third-color);
	padding: 8px;
	text-align: left;
}

///* Apply styles to content within text-page-header and text-page__content */
.text-page :deep(.text-page-header p),
.text-page :deep(.text-page__content p) {
	margin-bottom: 15px;
	line-height: 1.2;
}

.text-page :deep(.text-page-header strong),
.text-page :deep(.text-page__content strong) {
	font-weight: bold;
}

.text-page :deep(.text-page-header em),
.text-page :deep(.text-page__content em) {
	font-style: italic;
}

.text-page :deep(.text-page-header ul),
.text-page :deep(.text-page__content ul) {
	list-style: disc;
	padding-left: 30px;
	margin-bottom: 15px;
}

.text-page :deep(.text-page-header ol),
.text-page :deep(.text-page__content ol) {
	list-style: decimal;
	padding-left: 30px;
	margin-bottom: 15px;
}

.text-page :deep(.text-page-header li),
.text-page :deep(.text-page__content li) {
	margin-bottom: 8px;
}

.text-page :deep(.text-page-header h1),
.text-page :deep(.text-page__content h1) {
	font-size: 2.2em;
	margin-bottom: 20px;
	font-weight: bold;
}

.text-page :deep(.text-page-header h2),
.text-page :deep(.text-page__content h2) {
	font-size: 1.8em;
	margin-bottom: 18px;
	font-weight: bold;
}

.text-page :deep(.text-page-header h3),
.text-page :deep(.text-page__content h3) {
	font-size: 1.5em;
	margin-bottom: 16px;
	font-weight: bold;
}

.text-page :deep(.text-page-header h4),
.text-page :deep(.text-page__content h4) {
	font-size: 1.3em;
	margin-bottom: 14px;
	font-weight: bold;
}

.text-page :deep(.text-page-header h5),
.text-page :deep(.text-page__content h5) {
	font-size: 1.1em;
	margin-bottom: 12px;
	font-weight: bold;
}

.text-page :deep(.text-page-header h6),
.text-page :deep(.text-page__content h6) {
	font-size: 1em;
	margin-bottom: 10px;
	font-weight: bold;
}

.text-page :deep(.text-page-header blockquote),
.text-page :deep(.text-page__content blockquote) {
	border-left: 4px solid var(--bg-third-color);
	padding-left: 20px;
	margin: 20px 0;
	font-style: italic;
}

.text-page :deep(.text-page-header hr),
.text-page :deep(.text-page__content hr) {
	border: 0;
	border-top: 1px solid var(--bg-third-color);
	margin: 20px 0;
}

.text-page :deep(.text-page-header table),
.text-page :deep(.text-page__content table) {
	width: 100%;
	border-collapse: collapse;
	margin: 20px 0;
}

.text-page :deep(.text-page-header th),
.text-page :deep(.text-page__content th),
.text-page :deep(.text-page-header td),
.text-page :deep(.text-page__content td) {
	border: 1px solid var(--bg-third-color);
	padding: 8px;
	text-align: left;
}
</style>
